'use client'
import * as React from 'react'
import { motion, useAnimation, useReducedMotion } from 'framer-motion'
import { tw } from 'utils/classnames'
import { Button } from '@repo/design-system-cf/components/button'
import { IconThumbUpAlt, IconThumbUp } from '@repo/design-system-cf/components/icons'

export function VoteButton({
  onClick,
  upvotes,
  isLiked,
  isActionLoading,
  size = 'small',
}: {
  onClick?: () => void
  upvotes: number
  isLiked: boolean
  isActionLoading?: boolean
  size?: 'small' | 'regular'
}) {
  const controls = useAnimation()
  const reduce = useReducedMotion()

  const playLikeAnim = React.useCallback(() => {
    if (reduce) {
      return
    }
    controls.start({
      y: [0, -30, 0],
      rotateZ: [0, -10, 10, 0],
      scale: [1, 1.1, 1],
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        times: [0, 0.3, 0.6, 1],
      },
    })
  }, [controls, reduce])

  const handleClick = React.useCallback(() => {
    // Animate only when the user is *adding* a like
    if (!isLiked && !isActionLoading) {
      void playLikeAnim()
    }
    onClick?.()
  }, [isLiked, isActionLoading, onClick, playLikeAnim])

  return (
    <Button
      variant="ghost"
      size={size}
      className={tw(
        'text-cf-violet border font-medium border-[#1313151F] gap-1 flex w-auto bg-cf-grey-50 flex-row items-center p-0 !px-2.5 rounded-[40px] hover:bg-cf-grey-200 hover:text-cf-violet',
        size === 'regular' ? 'h-[32px] min-h-8 !px-3 text-16' : 'h-[26px] min-h-7 !px-2.5 text-14',
        isLiked ? 'bg-cf-purple-200 hover:bg-cf-purple-300' : '',
      )}
      data-testid="clubhouse-discussion-likes-button"
      iconLeft={
        <motion.div initial={false} animate={controls} className="flex items-center justify-center">
          {isLiked ? (
            <IconThumbUp className={tw(size === 'regular' ? '!size-5' : '!size-4')} />
          ) : (
            <IconThumbUpAlt className={tw(size === 'regular' ? '!size-5' : '!size-4 -mt-0.5')} />
          )}
        </motion.div>
      }
      onClick={handleClick}
      loading={isActionLoading}
    >
      {upvotes}
    </Button>
  )
}
